import { useMemo, useState } from 'react';
import type {
  EgressClusterMemberEligibility,
  EgressLinkListEntry,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { usePatchEgressLinkDispatch } from '../../api/useEgressLines.ts';
import { useToast, coerceErrorMessage } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Chip,
  ConfirmDialog,
  Panel,
  Switch,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { memberReasonKey } from './labels.ts';

export function EgressClusterTab({
  links,
  members,
  nowMs = Date.now(),
}: {
  readonly links: readonly EgressLinkListEntry[];
  readonly members: readonly EgressClusterMemberEligibility[];
  readonly nowMs?: number;
}): JSX.Element {
  const t = useT();
  const reasonById = useMemo(() => {
    const map = new Map<string, EgressClusterMemberEligibility>();
    for (const member of members) map.set(member.egress_id, member);
    return map;
  }, [members]);
  const groups = useMemo(() => {
    const byRegion = new Map<string, EgressLinkListEntry[]>();
    for (const link of links) {
      const list = byRegion.get(link.region) ?? [];
      list.push(link);
      byRegion.set(link.region, list);
    }
    return [...byRegion.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([region, group]) => ({ region, members: group }));
  }, [links]);

  if (links.length === 0) {
    return (
      <Panel caption={t('egress_lines.tab.clusters')} padded testId="egress-cluster-tab">
        <p className="m-0 text-dense text-paper-low">{t('egress_lines.clusters.empty')}</p>
      </Panel>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="egress-cluster-tab">
      {groups.map((group) => (
        <Panel
          key={group.region}
          caption={t('egress_lines.clusters.title', { region: group.region, count: String(group.members.length) })}
          padded
          testId={`egress-cluster-${group.region}`}
        >
          <Table>
            <THead>
              <Tr>
                <Th>{t('egress_lines.links.id')}</Th>
                <Th>{t('egress_lines.links.status')}</Th>
                <Th>{t('egress_lines.clusters.heartbeat_age')}</Th>
                <Th>{t('egress_lines.links.dispatch')}</Th>
                <Th>{t('egress_lines.clusters.reason')}</Th>
              </Tr>
            </THead>
            <TBody>
              {group.members.map((link) => (
                <ClusterMemberRow
                  key={link.egress_id}
                  link={link}
                  health={reasonById.get(link.egress_id)}
                  nowMs={nowMs}
                />
              ))}
            </TBody>
          </Table>
        </Panel>
      ))}
    </div>
  );
}

function ClusterMemberRow({
  link,
  health,
  nowMs,
}: {
  readonly link: EgressLinkListEntry;
  readonly health: EgressClusterMemberEligibility | undefined;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const patch = usePatchEgressLinkDispatch();
  const toast = useToast();
  const [confirmAdmit, setConfirmAdmit] = useState(false);
  const ageMs = Date.parse(link.last_heartbeat_at);
  const ageSeconds = Number.isFinite(ageMs) ? Math.max(0, Math.round((nowMs - ageMs) / 1000)) : null;

  async function apply(dispatchEnabled: boolean): Promise<void> {
    try {
      await patch.mutateAsync({ egressId: link.egress_id, dispatchEnabled });
      toast.success(
        dispatchEnabled
          ? t('egress_lines.links.dispatch.toast.enabled')
          : t('egress_lines.links.dispatch.toast.disabled'),
      );
    } catch (err) {
      toast.error(err instanceof OpsApiError ? err.message : coerceErrorMessage(err));
    } finally {
      setConfirmAdmit(false);
    }
  }

  return (
    <Tr data-testid={`egress-cluster-member-${link.egress_id}`}>
      <Td className="font-mono text-dense">{link.egress_id}</Td>
      <Td>
        <Chip tone={derivedTone(link.derived_status)} lamp={false}>{link.derived_status}</Chip>
      </Td>
      <Td className="font-mono text-micro text-paper-low">
        {ageSeconds === null
          ? t('egress_lines.clusters.age_unknown')
          : t('egress_lines.clusters.age_seconds', { seconds: String(ageSeconds) })}
      </Td>
      <Td>
        <Switch
          checked={link.dispatch_enabled}
          onCheckedChange={(checked) => {
            if (checked) setConfirmAdmit(true);
            else void apply(false);
          }}
          label={link.dispatch_enabled
            ? t('egress_lines.links.dispatch.admitted')
            : t('egress_lines.links.dispatch.standby')}
          testId={`egress-cluster-dispatch-${link.egress_id}`}
        />
        <ConfirmDialog
          open={confirmAdmit}
          onOpenChange={(open) => { if (!open) setConfirmAdmit(false); }}
          title={t('egress_lines.links.dispatch.confirm.title')}
          body={t('egress_lines.links.dispatch.confirm.body', { id: link.egress_id })}
          confirmLabel={t('egress_lines.links.dispatch.confirm.admit')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          loading={patch.isPending}
          onConfirm={() => { void apply(true); }}
          testId={`egress-cluster-dispatch-confirm-${link.egress_id}`}
        />
      </Td>
      <Td className="text-dense text-paper-low">
        {health === undefined ? t('egress_lines.policies.fallback.na') : t(memberReasonKey(health.reason))}
      </Td>
    </Tr>
  );
}

function derivedTone(status: EgressLinkListEntry['derived_status']): ChipTone {
  if (status === 'active') return 'ok';
  if (status === 'degraded') return 'warn';
  return 'bad';
}
